// src/components/HomepageContentItem.js

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDoorloopjes } from '../context/DoorloopjesContext';

import { ReactComponent as ArticleIcon } from '../assets/icon-article.svg';
import { ReactComponent as NewsIcon } from '../assets/icon-news.svg';

// Helper: haal ID + (optionele) privacy hash uit een Vimeo URL
function getVimeoIdAndHash(url) {
  if (!url) return {};
  const match = /vimeo\.com\/(\d+)(?:\/([a-z0-9]+))?/i.exec(url);
  return match ? { id: match[1], hash: match[2] || null } : {};
}

const HomepageContentItem = ({ item, type, alignment, onLoginClick }) => {
  const navigate = useNavigate();
  const { currentUser } = useDoorloopjes();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  let imageUrl = null;
  let iconComponent = null;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BELANGRIJK: altijd eerst de VOLLEDIGE afbeelding gebruiken, en alleen
  // terugvallen op thumbnail als die ontbreekt. Dit maakt de webinar-plaatjes
  // (digidates) weer "normale" grootte op de landingspagina.
  if (type === 'nieuws-item' || type === 'artikel') {
    imageUrl =
      item.featured_image?.url ||
      item.featured_image?.formats?.thumbnail?.url ||
      null;
    iconComponent = type === 'nieuws-item' ? NewsIcon : ArticleIcon;
  } else if (type === 'doorloopje-van-de-maand') {
    imageUrl =
      item.related_doorloopje?.steps?.[0]?.image?.url ||
      item.related_doorloopje?.steps?.[0]?.image?.formats?.thumbnail?.url ||
      null;
  } else if (type === 'digidates') {
    // Voor digidates gebruiken we geen imageUrl, maar tonen we een SVG illustratie
    imageUrl = null;
    iconComponent = null;
  }

  // Handler voor de publieke SEO-pagina knop
  const handlePublicPageClick = () => {
    if (item.public_page?.slug) {
      navigate(`/info/${item.public_page.slug}`);
    }
  };

  // Handler voor "Meer info" - naar publieke detailpagina voor nieuws-items, anders beveiligde pagina
  const handleMoreInfoClick = () => {
    if (type === 'nieuws-item') {
      // Ga direct naar de publieke nieuws detailpagina
      navigate(`/nieuws/${item.slug}`);
    } else if (type === 'artikel') {
      // Ga direct naar de publieke artikel detailpagina
      navigate(`/artikel/${item.slug}`);
    } else {
      // Voor andere types (digidates), blijf oude gedrag gebruiken
      const targetType = (type === 'digidates') ? 'webinars' : type;
      const targetPath = `/${targetType}/${item.slug}`;

      if (!currentUser) {
        onLoginClick(
          targetPath,
          'Log in om dit te bekijken.'
        );
      } else {
        navigate(targetPath);
      }
    }
  };

  const sectionClass = `content-section align-${alignment} ${isVisible ? 'is-visible' : ''} ${type === 'digidates' ? 'digidate-section' : ''}`;

  // Check of er een publieke pagina gekoppeld is (voor alle content types)
  const hasPublicPage = item.public_page && item.public_page.slug;

  return (
    <section className={sectionClass} ref={sectionRef}>
      <div className="image-content" onClick={handleMoreInfoClick} style={{ cursor: 'pointer' }}>
        {type === 'digidates' ? (
          // Voor digidates tonen we een SVG illustratie met gele achtergrond
          <div className="digidate-svg-wrapper">
            <img src="/webinar-illustration.svg" alt="Webinar illustratie" />
          </div>
        ) : imageUrl ? (
          // Grote afbeelding; laat CSS de breedte bepalen.
          <img src={imageUrl} alt={item.title} loading="lazy" />
        ) : iconComponent ? (
          <div className="homepage-item-icon-wrapper">
            {React.createElement(iconComponent)}
          </div>
        ) : (
          <div className="image-placeholder" style={{width: '100%', height: '200px', backgroundColor: '#e9ecef', borderRadius: '8px'}}></div>
        )}
      </div>
      <div className="text-content">
        <p className="item-type-label">{type === 'digidates' ? 'WEBINAR' : type.replace(/-/g, ' ').toUpperCase()}</p>
        <h2>{item.title}</h2>
        <p>{item.summary}</p>
        <div className="button-group">
          {type === 'digidates' && (
            item.has_taken_place && item.recording_url ? (
              (() => {
                const { id: vimeoId, hash: vimeoHash } = getVimeoIdAndHash(item.recording_url);
                if (vimeoId) {
                  return (
                    <div className="video-player-wrapper homepage-video-player">
                      <iframe
                        src={`https://player.vimeo.com/video/${vimeoId}${
                          vimeoHash ? `?h=${vimeoHash}&` : '?'
                        }color=ffdd00&title=0&byline=0&portrait=0`}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title={item.title}
                      ></iframe>
                    </div>
                  );
                }
                return (
                  <a
                    href={item.recording_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    Bekijk de opname
                  </a>
                );
              })()
            ) : !item.has_taken_place && item.registration_url ? (
              <a
                href={item.registration_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Meld je aan
              </a>
            ) : null
          )}

          {/* Meer info knop - altijd aanwezig */}
          <button className="btn-secondary" onClick={handleMoreInfoClick}>Meer info</button>

          {/* Publieke SEO-pagina knop (alleen als public_page gekoppeld is) - altijd als laatste */}
          {hasPublicPage && (
            <button className="btn-secondary" onClick={handlePublicPageClick}>
              {item.public_page.cta_label || 'Lees meer'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomepageContentItem;
